import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Star, Wand2, Camera, Settings, BarChart3, Newspaper, BadgeCheck, Footprints, Inbox, UserPlus, Users, Building2, LayoutDashboard, Calculator, Car, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, reportError, adminGet } from '../components/admin/adminApi';
import { AdminLogin } from '../components/admin/AdminLogin';
import { AdminShell } from '../components/admin/AdminShell';
import { AdminHomeTab } from '../components/admin/AdminHomeTab';
import { AdminLeadsTab } from '../components/admin/AdminLeadsTab';
import { AdminReviewsTab } from '../components/admin/AdminReviewsTab';
import { AdminCandidaturesTab } from '../components/admin/AdminCandidaturesTab';
import { AdminTeamTab } from '../components/admin/AdminTeamTab';
import { AdminPhotosTab } from '../components/admin/AdminPhotosTab';
import { AdminSettingsTab } from '../components/admin/AdminSettingsTab';
import { AdminImagesTab } from '../components/AdminImagesTab';
import { AdminAnalyticsTab } from '../components/admin/AdminAnalyticsTab';
import { AdminBlogTab } from '../components/admin/AdminBlogTab';
import { AdminEasersTab } from '../components/admin/AdminEasersTab';
import { AdminTractsTab } from '../components/admin/AdminTractsTab';
import { AdminAccompagnementsTab } from '../components/admin/AdminAccompagnementsTab';
import { AdminClientsTab } from '../components/admin/AdminClientsTab';
import { AdminDevisGreenTab } from '../components/admin/AdminDevisGreenTab';
import { AdminQualiteTab } from '../components/admin/AdminQualiteTab';

const SESSION_KEY = 'pts_admin_key';

/** Sections of the back-office, grouped for the sidebar. `description` feeds the top bar. */
const NAV_GROUPS = [
  {
    label: 'Pilotage',
    items: [
      { id: 'home', label: 'Accueil', icon: LayoutDashboard, description: "Chiffres clés du jour et actions en attente" },
      { id: 'analytics', label: 'Statistiques', icon: BarChart3, description: 'Visites, pages vues et conversions du site' },
      { id: 'leads', label: 'Leads', icon: Inbox, description: 'Demandes de devis, rendez-vous et messages à traiter' },
      { id: 'reviews', label: 'Avis clients', icon: Star, description: 'Avis déposés sur le site, à approuver ou rejeter' },
      { id: 'candidatures', label: 'Candidatures', icon: UserPlus, description: 'Candidats Easer et suivi du recrutement', testId: 'admin-tab-candidatures' },
    ],
  },
  {
    label: 'Contenu & SEO',
    items: [
      { id: 'blog', label: 'Blog', icon: Newspaper, description: 'Articles publiés et génération hebdomadaire' },
      { id: 'images', label: 'Visuels IA', icon: Wand2, description: 'Images générées pour les pages et les réseaux', testId: 'admin-tab-images' },
      { id: 'photos', label: 'Photos', icon: Camera, description: 'Photos de prestations affichées sur le site' },
      { id: 'team', label: 'Équipe', icon: Users, description: 'Membres présentés sur la page équipe' },
    ],
  },
  {
    label: 'Opérations',
    items: [
      { id: 'easers', label: 'Espace Easer', icon: BadgeCheck, description: 'Pointages, demandes GRH, notifications et moral des Easers' },
      { id: 'tracts', label: 'Tracts', icon: Footprints, description: 'Tournées de distribution suivies par GPS' },
      { id: 'accompagnements', label: 'Accompagnements', icon: Car, description: 'Kilomètres des accompagnements (bouton ACC) à facturer', testId: 'admin-tab-accompagnements' },
      { id: 'clients', label: 'Clients', icon: Building2, description: 'Comptes du portail client, satisfaction et tickets' },
      { id: 'devis-green', label: 'Devis Green', icon: Calculator, description: 'Grille de la calculatrice de devis jardinage et devis générés par les managers', testId: 'admin-tab-devis-green' },
      { id: 'qualite', label: 'Contrôle qualité', icon: ClipboardCheck, description: 'Évaluations des interventions par les managers et grille des critères', testId: 'admin-tab-qualite' },
    ],
  },
  {
    label: 'Système',
    items: [
      { id: 'news', label: 'Paramètres', icon: Settings, description: 'E-mail SMTP, clés IA, Facebook et réglages du site', superOnly: true },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);
const DEFAULT_TAB = 'home';

/** Lightweight counters shown as sidebar badges; refreshed on auth and when a tab reports changes. */
function useAdminCounts(adminKey, authenticated) {
  const [counts, setCounts] = useState({ leads: null, reviews: null, candidatures: null });

  const refresh = useCallback(async () => {
    if (!authenticated) return;
    const results = await Promise.allSettled([
      adminGet('/api/admin/leads/stats', adminKey),
      adminGet('/api/admin/reviews', adminKey),
      adminGet('/api/admin/candidatures', adminKey),
    ]);
    const [stats, reviews, cands] = results;
    setCounts({
      leads: stats.status === 'fulfilled' ? stats.value.data.total_leads : null,
      reviews: reviews.status === 'fulfilled' ? (reviews.value.data.reviews || []).length : null,
      candidatures: cands.status === 'fulfilled' ? (cands.value.data.counts?.nouveau ?? null) : null,
    });
    results.filter((r) => r.status === 'rejected').forEach((r) => reportError('compteurs admin', r.reason));
  }, [adminKey, authenticated]);

  useEffect(() => { refresh(); }, [refresh]);

  return { counts, refresh, setCounts };
}

function badgeFor(item, counts) {
  if (item.id === 'leads' && counts.leads !== null) {
    return { label: counts.leads, ariaLabel: `Leads : ${counts.leads}`, testId: 'admin-sidebar-badge-leads' };
  }
  if (item.id === 'reviews' && counts.reviews) {
    return { label: counts.reviews, ariaLabel: `Avis clients : ${counts.reviews}`, testId: 'admin-sidebar-badge-reviews' };
  }
  if (item.id === 'candidatures' && counts.candidatures) {
    return { label: `${counts.candidatures} nouv.`, tone: 'new', ariaLabel: `Candidatures : ${counts.candidatures} nouvelles`, testId: 'admin-sidebar-badge-candidatures' };
  }
  return null;
}

export default function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(SESSION_KEY) || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [restoring, setRestoring] = useState(() => Boolean(sessionStorage.getItem(SESSION_KEY)));
  const [adminRole, setAdminRole] = useState('super');
  const [adminAgency, setAdminAgency] = useState(null);
  const { counts, setCounts } = useAdminCounts(adminKey, authenticated);

  const requestedTab = searchParams.get('tab');
  const activeTab = ALL_ITEMS.some((t) => t.id === requestedTab) ? requestedTab : DEFAULT_TAB;
  const setActiveTab = useCallback((id) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', id);
      return next;
    });
  }, [setSearchParams]);

  const authenticate = useCallback(async (key, { silent = false } = {}) => {
    const candidate = (key ?? '').trim();
    if (!candidate) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/auth`, { params: { key: candidate } });
      setAdminRole(res.data.role);
      setAdminAgency(res.data.agency);
      setAuthenticated(true);
      sessionStorage.setItem(SESSION_KEY, candidate);
    } catch (err) {
      sessionStorage.removeItem(SESSION_KEY);
      if (!silent) reportError('authentification', err, () => toast.error('Clé admin invalide'));
    } finally {
      setRestoring(false);
    }
  }, []);

  // Restore the session after a page reload (key kept for this browser tab only).
  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) authenticate(saved, { silent: true });
  }, [authenticate]);

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthenticated(false);
    setAdminKey('');
    toast.success('Déconnecté');
  }, []);

  const onSessionExpired = useCallback(() => { sessionStorage.removeItem(SESSION_KEY); setAuthenticated(false); }, []);
  const onLeadStats = useCallback((stats) => setCounts((c) => ({ ...c, leads: stats?.total_leads ?? c.leads })), [setCounts]);
  const onReviewCount = useCallback((n) => setCounts((c) => ({ ...c, reviews: n })), [setCounts]);
  const onCandidatureCount = useCallback((n) => setCounts((c) => ({ ...c, candidatures: n })), [setCounts]);

  const isSuper = adminRole === 'super';
  const groups = useMemo(() => NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((t) => !t.superOnly || isSuper).map((t) => ({ ...t, badge: badgeFor(t, counts) })) }))
    .filter((g) => g.items.length > 0), [isSuper, counts]);

  if (restoring) {
    return <div className="min-h-dvh bg-background" aria-busy="true" data-testid="admin-restoring" />;
  }

  if (!authenticated) {
    return <AdminLogin adminKey={adminKey} onChange={setAdminKey} onSubmit={() => authenticate(adminKey)} />;
  }

  const current = ALL_ITEMS.find((t) => t.id === activeTab) || ALL_ITEMS[0];
  const agencyName = adminAgency ? adminAgency.charAt(0).toUpperCase() + adminAgency.slice(1) : '';
  const roleLabel = isSuper ? 'Super Admin' : 'Admin agence';
  const agencyLabel = isSuper ? 'Accès complet' : agencyName;

  return (
    <AdminShell
      groups={groups}
      activeId={activeTab}
      onSelect={setActiveTab}
      title={current.label}
      description={current.description}
      roleLabel={roleLabel}
      agencyLabel={agencyLabel}
      onLogout={logout}
    >
      {activeTab === 'home' && <AdminHomeTab adminKey={adminKey} onNavigate={setActiveTab} />}
      {activeTab === 'analytics' && <AdminAnalyticsTab adminKey={adminKey} />}
      {activeTab === 'leads' && <AdminLeadsTab adminKey={adminKey} onSessionExpired={onSessionExpired} onStats={onLeadStats} />}
      {activeTab === 'reviews' && <AdminReviewsTab adminKey={adminKey} onCount={onReviewCount} />}
      {activeTab === 'candidatures' && <AdminCandidaturesTab adminKey={adminKey} onCount={onCandidatureCount} />}
      {activeTab === 'blog' && <AdminBlogTab adminKey={adminKey} />}
      {activeTab === 'images' && <AdminImagesTab adminKey={adminKey} />}
      {activeTab === 'team' && <AdminTeamTab adminKey={adminKey} isSuperAdmin={isSuper} />}
      {activeTab === 'easers' && <AdminEasersTab adminKey={adminKey} />}
      {activeTab === 'devis-green' && <AdminDevisGreenTab adminKey={adminKey} />}
      {activeTab === 'qualite' && <AdminQualiteTab adminKey={adminKey} />}
      {activeTab === 'tracts' && <AdminTractsTab adminKey={adminKey} />}
      {activeTab === 'accompagnements' && <AdminAccompagnementsTab adminKey={adminKey} />}
      {activeTab === 'clients' && <AdminClientsTab adminKey={adminKey} />}
      {activeTab === 'photos' && <AdminPhotosTab adminKey={adminKey} />}
      {activeTab === 'news' && isSuper && <AdminSettingsTab adminKey={adminKey} />}
    </AdminShell>
  );
}
